import * as resources from "../../constants/resources";
import * as permissions from "../../constants/permissions";

import User from "../entities/User";

/**
 * @class LoggedUser
 */
export default class LoggedUser extends User {
    /**
     * @param {Number}  id
     * @param {String}  login
     * @param {String}  barcode
     * @param {Boolean} local
     * @param {Boolean} isDeleted
     * @param {Boolean} canBeDeleted
     * @param {Boolean} canBeEdited
     * @param {Boolean} autoLogin
     * @param {Array}   groups
     */
    constructor(
        id,
        login,
        barcode = "",
        local = false,
        isDeleted = false,
        canBeDeleted = false,
        canBeEdited = false,
        autoLogin = false,
        groups = []
    ) {
        super(id, login, barcode, local, isDeleted, canBeDeleted, canBeEdited, autoLogin, groups);

        this._privileges = {};
    }

    /**
     * @return {Object}
     */
    get privileges() {
        return this._privileges;
    }

    /**
     * @param {Object} privileges
     */
    set privileges(privileges) {
        this._privileges = privileges;
    }

    /**
     * User has no privilege to access given resource.
     *
     * @return {Boolean}
     */
    canNotRead = (resource) => {
        if (this._privileges[resource] === undefined) {
            return true;
        }

        return this._privileges[resource] === permissions.HIDDEN;
    };

    /**
     * User has privilege to access given resource.
     *
     * @return {Boolean}
     */
    canRead = (resource) => {
        if (this._privileges[resource] === undefined) {
            return false;
        }

        return this._privileges[resource] > permissions.HIDDEN;
    };

    /**
     * User has no privilege to edit given resource.
     *
     * @return {Boolean}
     */
    canNotEdit = (resource) => {
        if (this._privileges[resource] === undefined) {
            return true;
        }

        return this._privileges[resource] < permissions.AVAILABLE;
    };

    /**
     * User has privilege to edit given resource.
     *
     * @return {Boolean}
     */
    canEdit = (resource) => {
        return this._privileges[resource] === permissions.AVAILABLE;
    };

    /**
     * Add privilege to given resource.
     */
    addPrivilege(resource, permission) {
        this._privileges[resource] = permission;
    }

    /**
     * @return {LoggedUser}
     */
    clone = () => {
        let clone = new LoggedUser(
            this._id,
            this._login,
            this._barcode,
            this._local,
            this._isDeleted,
            this._canBeDeleted,
            this._canBeEdited,
            this._autoLogin,
            this._groups
        );

        clone.privileges = this._privileges;

        return clone;
    };

    /**
     * @param   {Object}        privileges
     * @return  {LoggedUser}
     */
    updatePrivileges = (privileges) => {
        let clone = this.clone();
        clone.privileges = privileges;

        return clone;
    };

    /**
     * @param   {Object}        privileges
     * @return  {LoggedUser}
     */
    logOut = (privileges) => {
        let logOutUser = new LoggedUser(0, "");
        logOutUser.privileges = privileges;

        return logOutUser;
    };

    /**
     * Function is only for test purpose.
     *
     * @return  {Boolean}
     */
    areGammaPrivilegesValid = () => {
        if (this._login === "user") {
            if (
                this.canNotEdit(resources.FUNCTION_CLOSE_REDLIKE) &&
                this.canNotEdit(resources.FUNCTION_SWITCH_TO_REDLIKE) &&
                this.canEdit(resources.COMPONENT_POS_INFO) &&
                this.canEdit(resources.COMPONENT_REGISTRATION) &&
                this.canNotEdit(resources.SETTINGS_FORMULA_UNITS) &&
                this.canEdit(resources.SETTINGS_LOCALE) &&
                this.canEdit(resources.SETTINGS_SCREENSAVER_TIMEOUT) &&
                this.canEdit(resources.SETTINGS_AUTOMATIC_PRINT) &&
                this.canEdit(resources.SETTINGS_PRINT_BEFORE_DISPENSING) &&
                this.canEdit(resources.SETTINGS_PRINT_AFTER_DISPENSING) &&
                this.canNotEdit(resources.COMPONENT_AUTOTASKS_SETTINGS) &&
                this.canEdit(resources.FUNCTION_SET_CANISTER_LEVEL)
            ) {
                return true;
            }

            return false;
        }

        if (this._login === "admin") {
            if (
                this.canEdit(resources.FUNCTION_CLOSE_REDLIKE) &&
                this.canEdit(resources.FUNCTION_SWITCH_TO_REDLIKE) &&
                this.canEdit(resources.COMPONENT_POS_INFO) &&
                this.canEdit(resources.COMPONENT_REGISTRATION) &&
                this.canEdit(resources.SETTINGS_FORMULA_UNITS) &&
                this.canEdit(resources.SETTINGS_LOCALE) &&
                this.canEdit(resources.SETTINGS_SCREENSAVER_TIMEOUT) &&
                this.canEdit(resources.SETTINGS_AUTOMATIC_PRINT) &&
                this.canEdit(resources.SETTINGS_PRINT_BEFORE_DISPENSING) &&
                this.canEdit(resources.SETTINGS_PRINT_AFTER_DISPENSING) &&
                this.canEdit(resources.COMPONENT_AUTOTASKS_SETTINGS) &&
                this.canEdit(resources.FUNCTION_SET_CANISTER_LEVEL)
            ) {
                return true;
            }

            return false;
        }

        return false;
    };

    /**
     * @return {String}
     */
    toString() {
        return "LoggedUser (" + this._id + ")";
    }
}
